import React, { useState, useEffect } from 'react';
import { Box, Card, CardMedia, Typography, CircularProgress, Alert } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from './PageTitle';
import { useThemeMode } from '../contexts/ThemeContext';
import { exploreCategoriesService, CMSExploreCategory } from '../services/exploreCategories';
import { subcategoriesService } from '../services/subcategories';
import { resolveExploreCategoryRoute } from '../utils/categoryRoutes';
import { useAuth } from '../contexts/AuthContext';
import { resolveImageUrl, withImageFallback } from '../utils/imageFallback';

interface ExploreCategory {
  id: string;
  title: string;
  image: string;
  route: string | null;
}

const ExploreSection: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { mode } = useThemeMode();
  const { user } = useAuth();
  const isDark = mode === 'dark';
  const isAdmin = user?.role === 'admin';
  const [exploreCategories, setExploreCategories] = useState<ExploreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [{ data, error }, subsRes] = await Promise.all([
          exploreCategoriesService.getCategories(true),
          subcategoriesService.getActiveSubcategories(),
        ]);
        const subcategories = subsRes.data ?? [];
        if (!error && data) {
          const mappedCategories: ExploreCategory[] = data.map((cat: CMSExploreCategory) => {
            let title = cat.title_en || cat.title_key;
            const lang = i18n.language;
            if (lang === 'ar' && cat.title_ar) {
              title = cat.title_ar;
            } else if (lang === 'fr' && cat.title_fr) {
              title = cat.title_fr;
            }
            return {
              id: cat.id || '',
              title,
              image: cat.image_url,
              route: resolveExploreCategoryRoute(cat, subcategories),
            };
          });
          setExploreCategories(mappedCategories);
        }
      } catch (error) {
        console.error('Error fetching explore categories:', error);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [i18n.language]);

  const handleCategoryClick = (category: ExploreCategory) => {
    if (category.route) {
      navigate(category.route);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!exploreCategories || exploreCategories.length === 0) {
    if (isAdmin) {
      return (
        <Box sx={{ py: 4 }}>
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
            Explore categories are not configured. Go to{' '}
            <Box
              component="span"
              sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
              onClick={() => navigate('/admin/cms')}
            >
              Admin → CMS
            </Box>{' '}
            to manage explore section categories.
          </Alert>
        </Box>
      );
    }
    return null;
  }

  return (
    <Box sx={{ py: 6 }}>
      <PageTitle
        text={t('exploreSection.exploreTitle')}
        align="left"
        subtitle={t('exploreSection.exploreSubtitle')}
        size="large"
        glowEffect
      />
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)'
        },
        gap: { xs: 2, md: 3 },
        mt: { xs: 3, md: 4 }
      }}>
        {exploreCategories.map((category) => (
          <Card
            key={category.id}
            component="button"
            type="button"
            onClick={() => handleCategoryClick(category)}
            disabled={!category.route}
            aria-label={category.title}
            sx={{
              position: 'relative',
              height: { xs: 220, sm: 250, md: 280 },
              cursor: 'pointer',
              overflow: 'hidden',
              border: 'none',
              padding: 0,
              textAlign: 'left',
              bgcolor: isDark ? '#111827' : 'oklch(98.7% 0.026 102.212)',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderStyle: 'solid',
              borderWidth: '1px',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                boxShadow: isDark ? '0 20px 40px rgba(0, 212, 255, 0.15)' : '0 20px 40px rgba(0, 0, 0, 0.15)',
                '& .category-image': { transform: 'scale(1.1)' },
                '& .category-overlay': { opacity: 1 },
                '& .category-info': { transform: 'translateY(0)', opacity: 1 },
              },
              '&:focus-visible': {
                outline: '3px solid #00d4ff',
                outlineOffset: 3,
              },
            }}
          >
            <CardMedia
              component="img"
              image={resolveImageUrl(category.image, '/placeholder-product.png')}
              alt={category.title}
              className="category-image"
              onError={(e) => withImageFallback(e, '/placeholder-product.png')}
              sx={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
            <Box
              className="category-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                opacity: 0.7,
                transition: 'opacity 0.4s ease',
              }}
            />
            <Box
              className="category-info"
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 3,
                transform: 'translateY(20px)',
                opacity: 0.9,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {category.title}
                </Typography>
                <Box
                  aria-hidden="true"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.16)' : '#1f2937',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.12)',
                    color: '#ffffff',
                    transition: 'all 0.2s ease',
                    '.MuiCard-root:hover &': {
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.26)' : '#111827',
                      transform: 'scale(1.1)',
                      border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  <ArrowForward fontSize="small" />
                </Box>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ExploreSection;
